import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { catchError, Subscription, throwError } from 'rxjs';
import { ChartComponent } from 'ng-apexcharts';
import { ChartConfigService } from 'src/app/core/services/utelize/general/chart-config-service';
import { DeviceEndOfLifePerYearDTO } from 'src/app/core/models/system/widget/widget-device-end-of-life-per-year.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { MdmDashboardWidgetTitles, MdmDeviceEndOfLifeNotSet } from 'src/app/core/consts';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { mdmUserDomainsDisplayText } from 'src/app/core/functions-generic/functions-generic-mdm';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

@Component({
  selector: 'app-chart-mdm-device-end-of-life-year-count',
  templateUrl: './chart-mdm-device-end-of-life-year-count.component.html',
  styleUrls: ['./chart-mdm-device-end-of-life-year-count.component.scss'],
  providers: [TippyToolTipService]
})

export class ChartMdmDeviceEndOfLifeYearCountComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  @Input() allowContainerClose: boolean | undefined;
  @Input() chartHeight        : number  | undefined;
  @Input() chartTitle         : string  | undefined;
  @Input() containerGrow      : boolean | undefined;
  @Input() deviceClass        : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showTitleHintText  : boolean | undefined;
  @Input() userDomains        : string  | undefined;
  
  @Output() barClick        : EventEmitter<{chartName: string, year: string, count: number}>=new EventEmitter<{chartName: string, year: string, count: number}>();
  @Output() containerClosed : EventEmitter<void                                            >=new EventEmitter<void                                            >();
  @Output() userDomainsClear: EventEmitter<void                                            >=new EventEmitter<void                                            >();
  
  @ViewChild('chartRef') chartRef!: ChartComponent;

  get userDomainsText() { return mdmUserDomainsDisplayText(this.exceptionsService, this.userDomains); }
 
  private buildDevicesEndOfLifeChart: boolean=true;
  private filterCompanyID           : number       | undefined;
  private filterDate                : OurDate      | undefined=populateOurDate(this.exceptionsService, Date.now());
  private subDevicesEndOfLifeChart! : Subscription | undefined;
  private widgetDeviceCount         : number[]=[];
  private widgetEndOfLifePerYear    : string[]=[];
  
  public devicesEndOfLifeChart: any | undefined;
  public expandContainer      : boolean=true;
  public noData               : boolean=false;
  public visibleContainer     : boolean=true;

  constructor(
    private chartConfigService : ChartConfigService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService,
    private widgetService      : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      if (this.chartHeight==undefined)
        this.chartHeight=250;

      if (this.chartTitle==undefined)
        this.chartTitle="Devices End of Life (by Year)";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.buildDevicesEndOfLifeChart=true;
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (((changes['deviceClass'] && !changes['deviceClass'].firstChange)
           || (changes['userDomains'] && !changes['userDomains'].firstChange)
          ) && (this.filterCompanyID ?? -1)!=-1)
        this.getDevicesEndOfLife();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.unsubscribeDevicesEndOfLifeChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getCompanyDevicesEndOfLifeChart(filterCompanyID: number | undefined, filterDate: OurDate | undefined) {
    try {
      if (filterCompanyID==undefined)
        throw new Error("ChartMdmDeviceEndOfLifeYearCountComponent - CompanyID is undefined");

      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      this.getDevicesEndOfLife();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public getDevicesEndOfLife() {
    try {
      this.unsubscribeDevicesEndOfLifeChart();
      this.subDevicesEndOfLifeChart=new Subscription();

      const companyID=<number>(this.filterCompanyID   ?? 0);
      const year     =<number>(this.filterDate?.year  ?? 1);
      const month    =<number>(this.filterDate?.month ?? 1);
      const day      =<number>(this.filterDate?.day   ?? 1);
      
      const devicesEndOfLifeUpdatesSubscription=this.widgetService.getIntuneDevicesEndOfLifePerYearCountUpdates(companyID, this.deviceClass, this.userDomains)
      .pipe(
        catchError(error =>
          throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        )
      )
      .subscribe({
        next : data => this.updateDevicesEndOfLifeChart(data),
        error: error => this.exceptionsService.handleException(new Error(error))
      });

      this.subDevicesEndOfLifeChart.add(devicesEndOfLifeUpdatesSubscription);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private updateDevicesEndOfLifeChart(data: DeviceEndOfLifePerYearDTO[]) {
    try {
      this.widgetEndOfLifePerYear=[];
      this.widgetDeviceCount     =[];

      this.noData=(data ?? []).length===0;

      if (this.noData) {
        this.buildDevicesEndOfLifeChart=true;
        this.devicesEndOfLifeChart     =undefined;
        return;
      }

      const sorted=[...data].sort((a, b) => Number(a.year)-Number(b.year));

      for (let d of sorted) {
        const isNotSetBucket=Number(d.year)===MdmDeviceEndOfLifeNotSet.Year;

        this.widgetEndOfLifePerYear.push(isNotSetBucket
                                         ? MdmDeviceEndOfLifeNotSet.ChartLabel
                                         : <string><unknown>d.year);

        const count=Number(d.deviceCount);
        this.widgetDeviceCount.push(Number.isFinite(count) ? count : 0);
      }

      if (this.buildDevicesEndOfLifeChart) {
        this.buildDevicesEndOfLifeChart=false;
        
        //this.devicesEndOfLifePerYearChart('["--vz-primary", "--vz-success", "--vz-danger"]');
        this.devicesEndOfLifePerYearChart('["--vz-suresim-blue", "--vz-success", "--vz-danger"]');
      }

      if (this.chartRef?.updateSeries) {

        const scatterData=this.widgetEndOfLifePerYear.map((year, i) => ({
          x: year,
          y: this.widgetDeviceCount[i]
        }));

        const series=[
          {
            name: 'Devices',
            data: this.widgetDeviceCount,
            type: 'column'
          },
          {
            name: 'HitTargets',
            data: scatterData,
            type: 'scatter'
          },
        ];

        this.chartRef.updateSeries(series, true);

        this.chartRef.updateOptions({
          chart: {
            zoom: { enabled: false },
            pan : { enabled: false }
          },
          // dataLabels: {
          //   enabledOnSeries: [0],
          //   formatter      : (value: number) => (value ? "" : "0"),
          //   offsetY        : -6,
          //   style          : { colors: ['var(--vz-body-color)'], fontWeight: 600 },
          //   textAnchor     : 'middle'
          // },
          dataLabels : this.buildDataLabels(),
          plotOptions: this.buildPlotOptions(),
          yaxis      : this.buildYAxis(),
          labels     : this.widgetEndOfLifePerYear,
          legend     : {
            show               : false,
            showForSingleSeries: false,
            showForNullSeries  : false,
            showForZeroSeries  : false,
            onItemClick        : { toggleDataSeries: false },
            onItemHover        : { highlightDataSeries: false }
          },
          markers: {
            hover: { size: [0, 18] },
            size : [0, 16]
          },
          stroke: {
            curve: 'smooth',
            width: [0, 0]
          },
          tooltip: {
            enabledOnSeries: [0]
          },
          xaxis : {
            categories: this.widgetEndOfLifePerYear,
            type      : 'category'
          }
        }, false);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public unsubscribeDevicesEndOfLifeChart() {
    try {
      if (this.subDevicesEndOfLifeChart) {
        this.subDevicesEndOfLifeChart.unsubscribe();
        this.subDevicesEndOfLifeChart=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private devicesEndOfLifePerYearChart(colors: any) {
    try {
      colors=this.chartConfigService.resolveColours(colors);

      const scatterData=this.widgetEndOfLifePerYear.map((year, i) => ({
        x: year,
        y: this.widgetDeviceCount[i]
      }));

      const series=[
        {
          name: 'Devices',
          data: this.widgetDeviceCount,
          type: 'column'
        },
        {
          name: 'HitTargets',
          data: scatterData,
          type: 'scatter'
        },
      ];

      // const yaxis: any[]=[
      //   {
      //     title: {
      //       style: {
      //         fontWeight: 600,
      //       },
      //       text: 'Devices'
      //     },
      //   }
      // ];

      this.devicesEndOfLifeChart={
        chart: {
          events: {
            dataPointSelection: (event: any, chartContext: any, config: any) => {
              this.onBarClick(event, chartContext, config);
            }
          },
          height : this.chartHeight,
          pan: {
            enabled: false
          },
          toolbar: {
              show: false,
          },
          type: 'line',
          zoom: {
            enabled: false
          },
        },
        colors: colors,
        // dataLabels: {
        //   enabledOnSeries: [0],
        //   formatter      : (value: number) => (value ? "" : "0"),
        //   offsetY        : -6,
        //   style          : { colors: ['var(--vz-body-color)'], fontWeight: 600 },
        //   textAnchor     : 'middle'
        // },
        dataLabels: this.buildDataLabels(),
        fill      : {
          opacity: [1, 0]
        },
        labels: this.widgetEndOfLifePerYear,
        legend: {
          show               : false,
          showForSingleSeries: false,
          showForNullSeries  : false,
          showForZeroSeries  : false,
          onItemClick        : { toggleDataSeries: false },
          onItemHover        : { highlightDataSeries: false }
        },
        markers: {
          hover: { size: [0, 18] },
          size : [0, 16]
        },
        // plotOptions: {
        //   bar: {
        //     columnWidth: '90%'
        //   }
        // },
        plotOptions: this.buildPlotOptions(),
        series     : series,
        stroke     : {
          curve: 'smooth',
          width: [0, 0]
        },
        title: {
          style: {
            fontWeight: 600,
          },
          text: undefined
        },
        tooltip: {
          enabledOnSeries: [0]
        },
        xaxis: {
          categories: this.widgetEndOfLifePerYear,
          type      : 'category'
        },
        yaxis: this.buildYAxis()
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildDataLabels(): any {
    try {
      return {
        enabledOnSeries: [0],
        formatter      : (value: number) => (value ? Number(value).toLocaleString() : ""),
        offsetY        : 4,
        style          : { colors: ['#fff'], fontSize: '11px', fontWeight: 600 },
        textAnchor     : 'middle'
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { enabled: false };
    }
  }

  private buildPlotOptions(): any {
    try {
      return {
        bar: {
          borderRadius           : 6,
          borderRadiusApplication: 'end',
          columnWidth            : this.widgetEndOfLifePerYear.length>12 ? '60%' : '40px',
          dataLabels             : { position: 'top' }
        }
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { bar: { columnWidth: '60%' } };
    }
  }

  private buildYAxis(): any[] {
    try {
      const max=Math.max(0, ...this.widgetDeviceCount);

      return [
        {
          forceNiceScale: true,
          labels        : { formatter: (value: number) => Math.round(value).toLocaleString() },
          max           : max>0 && max<=10 ? max : undefined,
          min           : 0,
          tickAmount    : max>0 && max<=10 ? max : undefined,
          title         : { style: { fontWeight: 600 }, text: 'Devices' }
        }
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [{ title: { text: 'Devices' } }];
    }
  }

  public onBarClick(event: any, chartContext: any, config: any): void {
    try {
      const dataPointIndex=config?.dataPointIndex;

      if (dataPointIndex==undefined || dataPointIndex<0)
        return;

      const year =this.widgetEndOfLifePerYear[dataPointIndex];
      const count=this.widgetDeviceCount[dataPointIndex];

      if (!count)
        return;

      this.barClick.emit({
        chartName: MdmDashboardWidgetTitles.chart_DevicesEndOfLife,
        year     : year,
        count    : count
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;

      if (this.expandContainer) {
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event('resize'));
          }
          catch(e) {
            this.exceptionsService.handleException(<Error>e);
          }
        }, 0);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
